/** PositionSizingPanel — approved qty/order value/max loss/limiting rule +
 * expandable constraints + concentration snapshot.
 */
export default function PositionSizingPanel({ sizing }) {
  if (!sizing) return null;
  const s = sizing.concentration_snapshot;
  return (
    <div className="mt-3 space-y-2" data-testid="sizing-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border/40">
        <div className="p-2 bg-card/40"><div className="label-cap">Approved Qty</div><div className="mono text-sm">{sizing.approved_qty}</div></div>
        <div className="p-2 bg-card/40"><div className="label-cap">Order Value</div><div className="mono text-sm">${(sizing.meta?.dollar_alloc || 0).toLocaleString()}</div></div>
        <div className="p-2 bg-card/40"><div className="label-cap">Max Loss</div><div className="mono text-sm pnl-down">-${sizing.meta?.max_loss || 0}</div></div>
        <div className="p-2 bg-card/40"><div className="label-cap">Limiting Rule</div><div className="mono text-[10px] text-signal-yellow uppercase">{sizing.limiting_rule}</div></div>
      </div>
      <details className="text-xs">
        <summary className="cursor-pointer text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-current">
          All constraints ({sizing.constraints?.length || 0})
        </summary>
        <div className="mt-2 space-y-1 border border-border/40 p-2">
          {(sizing.constraints || []).map((c, i) => (
            <div key={c.rule + i} className="flex items-center gap-2 mono text-[11px]">
              <span className={`w-1 h-1 ${c.rule === sizing.limiting_rule ? "bg-signal-yellow" : "bg-muted-foreground"}`} aria-hidden="true"/>
              <span className={c.rule === sizing.limiting_rule ? "text-signal-yellow" : "text-muted-foreground"}>{c.rule}</span>
              <span className="ml-auto">≤ {c.max_qty}</span>
            </div>
          ))}
        </div>
      </details>
      {s && <ConcentrationPanel snapshot={s}/>}
    </div>
  );
}

function ConcentrationPanel({ snapshot }) {
  const corr = snapshot.correlation || {};
  const highCorr = corr.high_correlation || [];
  const etf = snapshot.etf_overlap || {};
  const sec = snapshot.sector || {};
  return (
    <div className="border border-border/40 mt-2" data-testid="concentration-panel">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-border/40">
        <div className="p-2 bg-card/40">
          <div className="label-cap">Correlation</div>
          {highCorr.length === 0 ? (
            <div className="mono text-[11px] pnl-up mt-1" data-testid="corr-low">
              Low · none {corr.threshold ? `≥ ${corr.threshold}` : ""}
            </div>
          ) : (
            <div className="mono text-[11px] text-signal-yellow mt-1" data-testid="corr-high">
              {highCorr.length} very-high pair(s)
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {highCorr.slice(0, 2).map((p) => {
                  const peer = p.symbols.find((sy) => sy !== snapshot.symbol);
                  return <div key={peer}>{peer} · ρ={p.rho}</div>;
                })}
              </div>
            </div>
          )}
        </div>
        <div className="p-2 bg-card/40">
          <div className="label-cap">ETF Overlap</div>
          {!etf.available ? (
            <div className="mono text-[11px] text-muted-foreground mt-1" data-testid="etf-unavailable">
              {etf.reason === "not_an_etf" ? "N/A · not an ETF" : "Overlap data unavailable"}
            </div>
          ) : (
            <div className={`mono text-[11px] mt-1 ${etf.overlap_pct >= 40 ? "text-signal-yellow" : "pnl-up"}`} data-testid="etf-overlap">
              {etf.overlap_pct}% overlaps holdings
              {etf.duplicated_symbols?.length > 0 && (
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  Duplicates: {etf.duplicated_symbols.slice(0, 4).join(", ")}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="p-2 bg-card/40">
          <div className="label-cap">Sector — {sec.sector || "—"}</div>
          <div className="mono text-[11px] mt-1">
            {sec.current_pct}% → {sec.after_pct}%
          </div>
        </div>
      </div>
    </div>
  );
}
